import { SITE_URL } from "../_config";
import { APP_NAME, LOGO_URL } from "../_config/site_urls";

type SchemaObject = Record<string, any>;

const SITE_NAME = APP_NAME || "Plan Crafter";
const SITE_ORIGIN = SITE_URL || "https://www.plancrafter.tech";

const toAbsoluteUrl = (path?: string | null): string | undefined =>
    !path ? undefined : (path.startsWith("http") ? path : `${SITE_ORIGIN}${path}`);

// react-helmet-async's MetaSection JSON.stringify()'s the structuredData object as-is,
// which only drops `undefined` keys — `null`/'' still serialize. Schema.org optional
// fields must be omitted rather than emitted empty, so every builder below routes
// through this before returning.
const omitEmpty = (obj: SchemaObject): SchemaObject =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

// ---- Article-family: BlogPosting / NewsArticle / Article ------------------
export type ArticleFamilyType = "BlogPosting" | "NewsArticle" | "Article";

export interface ArticleStructuredDataInput {
    type: ArticleFamilyType;
    headline?: string | null;
    description?: string | null;
    authorName?: string | null;
    datePublished?: string | null;
    dateModified?: string | null;
    image?: string | null;
    url: string;
}

export function buildArticleStructuredData(input: ArticleStructuredDataInput): SchemaObject | undefined {
    if (!input.headline || !input.url) return undefined;

    return omitEmpty({
        "@context": "https://schema.org",
        "@type": input.type,
        headline: input.headline,
        description: input.description,
        author: input.authorName ? { "@type": "Person", name: input.authorName } : undefined,
        datePublished: input.datePublished,
        dateModified: input.dateModified,
        image: input.image,
        url: toAbsoluteUrl(input.url),
    });
}

// ---- Site-level: WebSite + Organization (Home) -----------------------------
export function buildHomeStructuredData(): SchemaObject {
    return {
        "@context": "https://schema.org",
        "@graph": [
            omitEmpty({ "@type": "WebSite", name: SITE_NAME, url: SITE_ORIGIN }),
            omitEmpty({
                "@type": "Organization",
                name: SITE_NAME,
                url: SITE_ORIGIN,
                logo: toAbsoluteUrl(LOGO_URL),
            }),
        ],
    };
}

// ---- ContactPage (Contact Us) ----------------------------------------------
export function buildContactPageStructuredData(url: string): SchemaObject {
    return omitEmpty({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        url: toAbsoluteUrl(url),
        about: omitEmpty({
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_ORIGIN,
            contactPoint: omitEmpty({
                "@type": "ContactPoint",
                contactType: "customer support",
                url: toAbsoluteUrl(url),
            }),
        }),
    });
}

// ---- CollectionPage + ItemList (Blog & News listing) -----------------------
export interface CollectionListItem {
    name: string;
    url: string;
}

export function buildCollectionPageStructuredData(
    name: string,
    url: string,
    items: CollectionListItem[]
): SchemaObject | undefined {
    if (!items || items.length === 0) return undefined;

    return omitEmpty({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name,
        url: toAbsoluteUrl(url),
        mainEntity: {
            "@type": "ItemList",
            itemListElement: items.map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: toAbsoluteUrl(item.url),
                name: item.name,
            })),
        },
    });
}
